import { ToastContext } from 'contexts'
import { useFormik } from 'formik'
import { useModal } from 'hooks'
import { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useApiKeysBySubnetService } from 'services/apiKey/useApiKeysBySubnetService'
import { useApiKeysService } from 'services/apiKey/useApiKeysService'
import { useCreateApiKeyService } from 'services/apiKey/useCreateApiKeyService'
import {
    useGetAccountSubnetApiServices,
    useUpdateSubnetApiKey,
} from 'services/subnetApiService/useAccountSubnetApiService'
import { useGetSubnetApiServices } from 'services/subnetApiService/useSubnetApiService'

import { apiKeyValidation } from 'utils/validationsSchema'

export const useCreateApiKey = ({
    callback,
    isSubnet,
}: {
    isSubnet?: boolean
    callback?: () => void
}) => {
    const { id: subnet_id } = useParams()

    const { setToast } = useContext(ToastContext)
    const { openModal } = useModal()
    const navigate = useNavigate()
    const { refetch: refetchApiKeys } = useApiKeysService()
    const { refetch: refetchApiKeysBySubnet } = useApiKeysBySubnetService({ id: subnet_id || '' })
    const [createApiKeyService] = useCreateApiKeyService()
    const [isLoading, setIsLoading] = useState(false)

    const initialValues = {
        name: '',
        description: '',
        account_subnet_ids: [],
    }

    const { updateSubnetApiKey } = useUpdateSubnetApiKey()
    const { data: account_subnets } = useGetAccountSubnetApiServices()

    //temporary hidden switcher solution
    const { data: subnet_apis } = useGetSubnetApiServices(subnet_id)

    const handleSubmit = async (values: {
        name: string
        description: string
        account_subnet_ids: string[]
    }) => {
        setIsLoading(true)
        try {
            const apiKeyInput = {
                name: values.name,
                description: values.description,
                account_subnet_ids: isSubnet ? [subnet_apis?.[0]?.id] : [],
            }

            const newApiKey = await createApiKeyService(apiKeyInput)
            const apiKeyId = newApiKey.id

            if (isSubnet) {
                const filtered_account_subnets = account_subnets?.filter((subnet: any) =>
                    values.account_subnet_ids?.some(
                        (account_subnet_id: string) =>
                            account_subnet_id === subnet.subnet_api_service_id,
                    ),
                )
                await Promise.all(
                    filtered_account_subnets?.map(async (subnet: any) => {
                        await updateSubnetApiKey(subnet.id, { api_key_id: apiKeyId })
                    }),
                )
            }
            // todo need cache update instead of refetch
            if (subnet_id) {
                await refetchApiKeysBySubnet()
            } else {
                await refetchApiKeys()
            }
            setToast({
                message: 'New API Key was created!',
                type: 'positive',
                open: true,
            })
            openModal({
                name: 'show-api-key-modal',
                data: {
                    token: newApiKey.token,
                },
            })

            if (callback) {
                callback()
            } else {
                navigate(`/api-key`)
            }
        } catch (e) {
            setToast({
                message: 'Failed to create API Key!',
                type: 'negative',
                open: true,
            })
        }
        setIsLoading(false)
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: async values => handleSubmit(values),
        validationSchema: apiKeyValidation,
    })

    return {
        formik,
        handleSubmit,
        isLoading,
    }
}
